// The farm's own look and content, changed by an Admin on Administration ->
// Appearance with no developer: colour theme, font, corners, logo, the
// website's photos and gallery, the scrolling news, contact details. Public
// (GET /public/site) - the website and login page show it before sign-in.
// Cached in this browser so the theme is on before the first paint.
import { useSyncExternalStore } from 'react';
import apiClient, { API_BASE_URL } from '../services/api';

export interface SiteTheme {
  preset: string;
  primary: string; // hex - menus, buttons, headings
  accent: string; // hex - highlights, badges
  font: 'Inter' | 'Poppins' | 'Roboto' | 'Open Sans' | 'Lato' | 'Noto Sans' | 'Mukta';
  corners: 'sharp' | 'normal' | 'round';
}

export interface GalleryPhoto {
  photo: string;
  section: string;
  caption: string;
}

export interface SiteContent {
  theme: SiteTheme;
  logo: string | null;
  photos: Record<string, string>;
  gallery_sections: string[];
  gallery: GalleryPhoto[];
  news_show: boolean;
  news_label: string;
  news_items: string[];
  news_seconds: number;
  app_notice_show: boolean;
  app_notice_items: string[];
  app_notice_seconds: number;
  hero_badge: string;
  hero_title: string;
  hero_text: string;
  login_tagline: string;
  address: string;
  phone: string;
  email: string;
  business_hours: string;
  visitors_show: boolean;
  visitors_label: string;
}

export const DEFAULT_THEME: SiteTheme = { preset: 'farm-green', primary: '#3f7d44', accent: '#d38f18', font: 'Inter', corners: 'normal' };

export const SITE_DEFAULTS: SiteContent = {
  theme: DEFAULT_THEME,
  logo: null,
  photos: {},
  gallery_sections: ['The Farm & Sheds', 'Dairy Cows', 'Poultry', 'Sheep Rearing', 'Natural Agriculture', 'Processing & Packaging', 'Farm Events'],
  gallery: [],
  news_show: true,
  news_label: 'News',
  news_items: [
    'Farm-fresh milk from our own herd — morning and evening',
    'Every product traceable from our farm to your table',
    'Regular and bulk orders welcome — call or WhatsApp us',
    'Plan a visit to the farm — get in touch to book a day',
    'Dairy · Poultry · Sheep · Natural farming — all on one farm',
  ],
  news_seconds: 45,
  app_notice_show: false,
  app_notice_items: [],
  app_notice_seconds: 40,
  hero_badge: 'Nurturing Life, Harvesting Prosperity',
  hero_title: 'Pure, ethical, farm-fresh products from Sri KalpaGavya',
  hero_text: 'An integrated dairy, livestock and sustainable farming enterprise — combining responsible dairy farming, poultry and sheep rearing with modern digital farm management practices.',
  login_tagline: 'Nurturing Life, Harvesting Prosperity.',
  address: 'Survey Number 144/1A, Muthsandra Village, Via Varthur, Bengaluru - 560087',
  phone: '+91 81056 78882',
  email: 'skg.dairyandfarms@gmail.com',
  business_hours: '',
  visitors_show: true,
  visitors_label: 'Website visits',
};

// ---- website visitor counter (home page) ----
export interface VisitStats { views: number; visitors: number; today_views: number; today_visitors: number }

let visitCall: Promise<VisitStats> | null = null;

/** Counts this visit once per browser session; a browser seen for the first time is also a new visitor. */
export function countVisit(): Promise<VisitStats> {
  // One call per page load, however many times the home page mounts.
  visitCall ??= sendVisit().catch((err) => { visitCall = null; throw err; });
  return visitCall;
}

async function sendVisit(): Promise<VisitStats> {
  let counted = false;
  let known = false;
  try {
    counted = sessionStorage.getItem('skg.visit') === '1';
    known = localStorage.getItem('skg.visitor') === '1';
  } catch {
    // storage blocked - count it anyway
  }
  if (counted) return getVisits();
  const { data } = await apiClient.post<VisitStats>('/public/site/visit', { new_visitor: !known });
  try {
    sessionStorage.setItem('skg.visit', '1');
    localStorage.setItem('skg.visitor', '1');
  } catch {
    // private window
  }
  return data;
}

export const getVisits = () => apiClient.get<VisitStats>('/public/site/visits').then((r) => r.data);

// Ready-made colour themes; "Custom" lets the Admin pick both colours.
export const THEME_PRESETS: { id: string; label: string; primary: string; accent: string }[] = [
  { id: 'farm-green', label: 'Farm Green (default)', primary: '#3f7d44', accent: '#d38f18' },
  { id: 'forest', label: 'Forest', primary: '#2e6b4f', accent: '#c9a227' },
  { id: 'ocean', label: 'Ocean Blue', primary: '#2371b0', accent: '#e7a92a' },
  { id: 'teal', label: 'Teal', primary: '#0f766e', accent: '#f59e0b' },
  { id: 'royal', label: 'Royal Purple', primary: '#6d4aa8', accent: '#e0a526' },
  { id: 'maroon', label: 'Maroon', primary: '#9b2c3a', accent: '#d9a441' },
  { id: 'earth', label: 'Earth Brown', primary: '#7a5230', accent: '#5f8f3e' },
  { id: 'slate', label: 'Slate Grey', primary: '#475569', accent: '#d97706' },
  { id: 'saffron', label: 'Saffron', primary: '#c2410c', accent: '#15803d' },
];

// The website's fixed photo places - each shows the built-in picture until
// one is uploaded for it.
export const PHOTO_SLOTS: { slot: string; label: string; group: string; fallback: string | null }[] = [
  { slot: 'home.hero', label: 'Home — main banner', group: 'Home page', fallback: '/hero/banner.png' },
  { slot: 'home.overview', label: 'Home — "Our Story" picture', group: 'Home page', fallback: '/hero/farm-overview.jpg' },
  { slot: 'about.overview', label: 'About — farm overview', group: 'About page', fallback: null },
  { slot: 'about.team', label: 'About — farm team / partners', group: 'About page', fallback: null },
  { slot: 'product.cow-milk', label: 'Cow Milk', group: 'Products', fallback: '/products/cow-milk.jpg' },
  { slot: 'product.dairy-by-products', label: 'Dairy By-Products', group: 'Products', fallback: '/products/dairy-byproducts.jpg' },
  { slot: 'product.hen-eggs', label: 'Hen Eggs', group: 'Products', fallback: '/products/hen-eggs.jpg' },
  { slot: 'product.sheep-meat', label: 'Sheep Meat', group: 'Products', fallback: '/products/sheep-meat.jpg' },
  ...['Babu MG', 'Nagesh Babu BV', 'Vinay Kumar T', 'Ramesh MD', 'Muralidhara MR', 'Nanjunda Reddy K', 'Ramesh Reddy'].map((name) => {
    const slug = slugify(name);
    return { slot: `partner.${slug}`, label: name, group: 'Partners', fallback: `/partners/${slug}.jpg` };
  }),
  { slot: 'login.picture', label: 'Login page — background picture', group: 'Login page', fallback: null },
];

/** "+91 81056 78882" -> "tel:+918105678882" */
export function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}

export function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

const CACHE_KEY = 'ilms.site';

function readCache(): SiteContent {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return SITE_DEFAULTS;
    const v = JSON.parse(raw);
    return { ...SITE_DEFAULTS, ...v, theme: { ...DEFAULT_THEME, ...v.theme } };
  } catch {
    return SITE_DEFAULTS;
  }
}

let current = readCache();
const listeners = new Set<() => void>();

function publish(next: SiteContent): void {
  current = next;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(next));
  } catch {
    // Storage blocked - still applies for this visit.
  }
  applyTheme(next.theme);
  applyFavicon();
  listeners.forEach((l) => l());
}

export function getSite(): SiteContent {
  return current;
}

/** Re-renders when the site content changes (after a save or a fresh load). */
export function useSite(): SiteContent {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => current);
}

export function mediaUrl(name: string): string {
  return `${API_BASE_URL}/public/site/media/${name}`;
}

/** The uploaded photo for a slot, or the built-in one. */
export function photoUrl(site: SiteContent, slot: string): string | null {
  const name = site.photos[slot];
  if (name) return mediaUrl(name);
  return PHOTO_SLOTS.find((s) => s.slot === slot)?.fallback ?? null;
}

export function logoUrl(site: SiteContent = current): string {
  return site.logo ? mediaUrl(site.logo) : '/logo-sm.jpeg';
}

// ---------- colours ----------
const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
// How far each shade is mixed towards white (+) or black (-) from the chosen colour.
const MIX: Record<number, number> = { 50: 0.92, 100: 0.84, 200: 0.68, 300: 0.48, 400: 0.24, 500: 0, 600: -0.2, 700: -0.36, 800: -0.5, 900: -0.6, 950: -0.78 };

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function shadeRgb(hex: string, shade: number): [number, number, number] {
  const rgb = hexToRgb(hex);
  const t = MIX[shade] ?? 0;
  return rgb.map((c) => Math.round(t >= 0 ? c + (255 - c) * t : c * (1 + t))) as [number, number, number];
}

export function shadeHex(hex: string, shade: number): string {
  return `#${shadeRgb(hex, shade).map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

const FONT_URLS: Record<string, string> = {
  Poppins: 'Poppins:wght@400;500;600;700',
  Roboto: 'Roboto:wght@400;500;700',
  'Open Sans': 'Open+Sans:wght@400;500;600;700',
  Lato: 'Lato:wght@400;700',
  'Noto Sans': 'Noto+Sans:wght@400;500;600;700',
  Mukta: 'Mukta:wght@400;500;600;700',
};

export function applyTheme(theme: SiteTheme = current.theme): void {
  const root = document.documentElement.style;
  const isDefault = theme.primary.toLowerCase() === DEFAULT_THEME.primary && theme.accent.toLowerCase() === DEFAULT_THEME.accent;
  for (const s of SHADES) {
    // The default theme keeps its hand-tuned shades from index.css.
    if (isDefault) {
      root.removeProperty(`--brand-green-${s}`);
      root.removeProperty(`--brand-gold-${s}`);
    } else {
      root.setProperty(`--brand-green-${s}`, shadeRgb(theme.primary, s).join(' '));
      root.setProperty(`--brand-gold-${s}`, shadeRgb(theme.accent, s).join(' '));
    }
  }
  const radius = { sharp: ['0.125rem', '0.25rem'], normal: ['0.5rem', '0.75rem'], round: ['0.875rem', '1.25rem'] }[theme.corners] ?? ['0.5rem', '0.75rem'];
  root.setProperty('--radius-md', radius[0]);
  root.setProperty('--radius-lg', radius[1]);
  root.setProperty('--font-sans', `'${theme.font}'`);
  if (FONT_URLS[theme.font] && !document.getElementById(`font-${slugify(theme.font)}`)) {
    const link = document.createElement('link');
    link.id = `font-${slugify(theme.font)}`;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${FONT_URLS[theme.font]}&display=swap`;
    document.head.appendChild(link);
  }
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', isDefault ? '#26502b' : shadeHex(theme.primary, 700));
}

function applyFavicon(): void {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (link) link.href = current.logo ? mediaUrl(current.logo) : '/logo.jpeg';
}

/** On start-up: apply the cached look now, then fetch the latest. */
export function startSite(): void {
  applyTheme();
  applyFavicon();
  apiClient.get<SiteContent>('/public/site')
    .then(({ data }) => {
      const next = { ...SITE_DEFAULTS, ...data, theme: { ...DEFAULT_THEME, ...data.theme } };
      if (JSON.stringify(next) !== JSON.stringify(current)) publish(next);
    })
    .catch(() => { /* offline - keep the cached look */ });
}

export async function saveSite(site: SiteContent): Promise<SiteContent> {
  const { data } = await apiClient.put<SiteContent>('/system-settings/appearance/site', site);
  publish({ ...SITE_DEFAULTS, ...data });
  return data;
}

export async function uploadSiteMedia(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<{ name: string }>('/system-settings/appearance/media', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return data.name;
}

/** Live preview while editing - shows a theme without saving it. */
export function previewTheme(theme: SiteTheme | null): void {
  applyTheme(theme ?? current.theme);
}
